'use client';

import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';

import { cn } from '@seller-hermes/utils/cn';

const Popover = PopoverPrimitive.Root;

const PopoverPortal = PopoverPrimitive.Portal;

const PopoverTrigger = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <PopoverPrimitive.Trigger
    ref={ref}
    // A guarda de `pointer-events` NÃO mora aqui — mora no `Content`, logo
    // abaixo. O Radix marca `data-state` nos DOIS, e no gatilho "fechado" é o
    // estado normal: a classe aqui deixava o botão surdo a ponteiro enquanto o
    // popover estivesse fechado, ou seja, sempre — não abria porque não recebia
    // clique, e não recebia clique porque não abria. Foi assim que "+" e
    // "Filtro" pararam de responder até ao hover.
    className={cn('data-[state=open]:bg-muted', className)}
    {...props}
  />
));
PopoverTrigger.displayName = PopoverPrimitive.Trigger.displayName;

/**
 * SEM animação de saída, e com o fechado ignorando ponteiro — pelo mesmo motivo
 * do `DropdownMenu`.
 *
 * O Radix desmonta o conteúdo no `animationend`. Com `animate-out` declarado e
 * esse evento não chegando, o nó fica montado e VISÍVEL depois de fechado.
 * Medido no local: ao clicar em "Cancelar" no formulário de editar
 * visualização, o `data-state` virava `closed` e o formulário continuava na
 * tela (`visivel: true`, `pointer-events: auto`) — parecia que Escape, clique
 * fora e o próprio Cancelar não faziam nada, quando na verdade o estado já
 * tinha fechado. O mesmo fantasma cobria o campo de nome e era o "Editar não
 * funciona".
 *
 * A animação de ABERTURA continua: é a que se percebe.
 */
const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = 'center', sideOffset = 4, ...props }, ref) => (
  <PopoverPrimitive.Content
    ref={ref}
    align={align}
    sideOffset={sideOffset}
    className={cn(
      'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:!pointer-events-none data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 min-w-[var(--radix-popover-trigger-width)] rounded-md border p-4 shadow-md outline-none',
      className,
    )}
    {...props}
  />
));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

const PopoverArrow = PopoverPrimitive.Arrow;

export default Object.assign(Popover, {
  Trigger: PopoverTrigger,
  Content: PopoverContent,
  Arrow: PopoverArrow,
  Portal: PopoverPortal,
});
